<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        class="searchInput"
        state="default"
        placeholder="Numéro, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <BasicButton
        @click="toggleMesDossiers"
        :active="!!mesDossiers"
        variant="ghost"
        label="Mes dossiers"
        iconName="profile-circle"
        size="large"
      />
      <div class="searchWrapper--actions">
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="left"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            label="Créer un nouveau dossier"
            size="large"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="top"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            label="Créer un nouveau dossier"
            icon-name="folder-add"
            size="large"
            width="full"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
      </div>
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Code (AKTO, Interne, Déca), nom du stagiaire..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
      <BasicButton
        @click="toggleMesDossiers"
        :active="!!mesDossiers"
        variant="ghost"
        label="Mes dossiers"
        iconName="profile-circle"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-7 col-md-18 col-sm-36"
        />

        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!isAdvancedFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              type="reverse"
              iconName="filter-remove"
              @click="removeFilters"
              :disabled="!isAdvancedFiltered"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="pageCount"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-if="pageSizeVisible"
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
      <div class="informationDetailWrapper">
        <div class="informationElem">
          <BasicIcon
            name="document-text"
            active
          />
          <BasicText size="body-m">Mandat de gestion</BasicText>
        </div>
        <div class="informationElem">
          <BasicIcon
            name="wallet"
            :active="false"
          />
          <BasicText size="body-m">Subrogation de paiement</BasicText>
        </div>
        <div class="informationElem">
          <BasicIcon
            name="danger"
            :active="false"
            class="danger"
          />
          <BasicText size="body-m">Alerte</BasicText>
        </div>
        <div class="informationElem">
          <BasicIcon
            name="profile-circle"
            :active="false"
          />
          <BasicText size="body-m">Mes dossiers</BasicText>
        </div>
      </div>
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <DossierCardMobile
          v-for="(item, index) in store.pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <DossierHeader v-model="order" />
        <DossierCardDesktop
          v-for="(item, index) in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Dossiers"
        headerText="Dossiers"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { StatutOutilFpeLibelle } from '@/features/domaine/types/opco/Dossier'
  import { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import { type NavigateToQueryDossierFormation, type WithNavigateToLocation } from '@/router/navigateTo'
  import { onBeforeUnmount } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { objectToDropdownItems } from '../../shared/tools'
  import WrapPermissions from '../../shared/wrap-permissions/WrapPermissions.vue'
  import { pageSizeItems } from '../shared/dropdown-items.ts'
  import { DossierCardDesktop, DossierCardMobile, DossierHeader } from './components'
  import { useRechercheDossierTtActions } from './useRechercheDossierTtActions'
  import { useRechercheDossierTtStore } from './useRechercheDossierTtStore'
  import { storeToRefs } from 'pinia'
  import { usePagination, useShowFilter } from '@/features/interface/recherches/shared'

  defineProps<WithNavigateToLocation<NavigateToQueryDossierFormation>>()

  const store = useRechercheDossierTtStore()
  onBeforeUnmount(store.$reset)
  const { setupFieldWithKey } = store
  const { order, search } = storeToRefs(store)
  const { showFilters, removeFilters, isAdvancedFiltered } = useShowFilter(store)
  const { pageCount, pageSize, pageNumber, pageChange, pageSizeVisible } = usePagination(store)

  const { addFavori, addDemande } = useRechercheDossierTtActions()
  const statutsItems = objectToDropdownItems(StatutOutilFpeLibelle)
  const statuts = setupFieldWithKey<string[]>('query.statuts')

  const mesDossiers = setupFieldWithKey<boolean | null | undefined>('query.estProprietaire')
  const toggleMesDossiers = () => {
    if (mesDossiers.value) {
      mesDossiers.value = undefined
    } else {
      mesDossiers.value = true
    }
  }

  /*
  const exporter = async () => {
    await store.printData()
    await useDialog().showDialog({
      type: 'Ok',
      title: 'Export des dossiers',
      message: 'Vos dossiers ont été exportés avec succès, vous allez les recevoir par mail',
      closable: true,
    })
  }
  */

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })
</script>

<style scoped>
  @import '../index.less';
</style>
